import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { getCompany, updateCompany, updateUser } from "../../services/firestore";
import PlacesAutocomplete from "../../components/PlacesAutocomplete";
import { getAccessToken, isGmailConnected, setGmailLinkedFlag, getGmailLinkedFlag, disconnectGmail } from "../../services/gmail";
import CalendarSubscribe from "../../components/CalendarSubscribe";
import "./OwnerLayout.css";

export default function Settings() {
  const { userProfile, loadUserProfile } = useAuth();
  // Owner's personal home-base address — used as the start AND end point
  // of mileage auto-calc on Ferrari and Executive jobs. Stored on the
  // user doc (not the company doc) because in a multi-owner future,
  // each owner would have their own home base. PlacesAutocomplete writes
  // both the address string and the resolved lat/lng so Maps calls don't
  // need to re-geocode each time.
  const [homeForm, setHomeForm] = useState({
    homeAddress: "",
    homeLat: null,
    homeLng: null,
  });
  const [savingHome, setSavingHome] = useState(false);
  const [homeSaved, setHomeSaved] = useState(false);

  // Which view greets the owner at login — their own owner dashboard, or
  // the guard view (handy when the owner also runs jobs themselves).
  // Saved on the user doc; the login redirect reads it.
  const [savingView, setSavingView] = useState(false);
  async function setDefaultView(view) {
    if (!userProfile?.id || savingView) return;
    setSavingView(true);
    try {
      await updateUser(userProfile.id, { defaultView: view });
      await loadUserProfile(userProfile.id);
    } catch (e) {
      window.alert("Couldn't save: " + (e?.message || e));
    }
    setSavingView(false);
  }

  const [form, setForm] = useState({
    name: "",
    hubAirport: "CLT",
    // Cargo-handler street address at the hub airport. Used everywhere we
    // need a real address for the airport stop: tap-to-navigate, mileage
    // calc, workbook output. Captured once here so jobs auto-fill correctly.
    hubAirportAddress: "",
    hubAirportLat: null,
    hubAirportLng: null,
    hubAirportNotes: "",
    inboundFlight: "",
    inboundArrivalTime: "05:15",
    outboundFlight: "",
    confirmReminderTime: "10:00",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  // Seed the home-base form from the loaded user profile.
  useEffect(() => {
    if (!userProfile) return;
    setHomeForm({
      homeAddress: userProfile.homeAddress || "",
      homeLat: userProfile.homeLat ?? null,
      homeLng: userProfile.homeLng ?? null,
    });
  }, [userProfile?.id, userProfile?.homeAddress]);

  async function handleSaveHome() {
    setSavingHome(true);
    try {
      await updateUser(userProfile.id, homeForm);
      // Refresh the cached profile so other screens (ReadyToBill) see
      // the new home address without needing a full reload.
      await loadUserProfile(userProfile.id);
      setHomeSaved(true);
      setTimeout(() => setHomeSaved(false), 2500);
    } catch (err) {
      console.error("Couldn't save home base", err);
    }
    setSavingHome(false);
  }

  useEffect(() => {
    if (!userProfile?.companyId) return;
    getCompany(userProfile.companyId).then((company) => {
      if (company) {
        setForm({
          name: company.name || "",
          hubAirport: company.hubAirport || "CLT",
          hubAirportAddress: company.hubAirportAddress || "",
          hubAirportLat: company.hubAirportLat ?? null,
          hubAirportLng: company.hubAirportLng ?? null,
          hubAirportNotes: company.hubAirportNotes || "",
          inboundFlight: company.inboundFlight || "",
          inboundArrivalTime: company.inboundArrivalTime || "05:15",
          outboundFlight: company.outboundFlight || "",
          confirmReminderTime: company.confirmReminderTime || "10:00",
        });
      }
      setLoading(false);
    });
  }, [userProfile]);

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function handleSave() {
    if (!form.name.trim()) return setError("Company name is required.");
    setSaving(true);
    setError("");
    try {
      await updateCompany(userProfile.companyId, form);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch {
      setError("Failed to save. Please try again.");
    }
    setSaving(false);
  }

  // ── Gmail integration state ────────────────────────────────────────
  const [gmailLinked, setGmailLinked] = useState(false);
  const [gmailBusy, setGmailBusy] = useState(false);
  const [gmailMsg, setGmailMsg] = useState("");
  useEffect(() => {
    if (!userProfile?.companyId) return;
    getGmailLinkedFlag(userProfile.companyId).then(setGmailLinked).catch(() => {});
  }, [userProfile?.companyId]);

  async function handleConnectGmail() {
    setGmailBusy(true);
    setGmailMsg("");
    try {
      // ALWAYS force the consent / account picker on Connect Gmail —
      // not just on first connect. With silent re-auth Google reuses
      // whichever account the browser session sits on, which is often
      // not the one the owner wants sending invoices. Making the
      // picker mandatory here means clicking Connect is the unambiguous
      // "switch account" action.
      await getAccessToken({ interactive: true, forceConsent: true });
      await setGmailLinkedFlag(userProfile.companyId, true);
      setGmailLinked(true);
      setGmailMsg("Connected. Approving a job will now drop a draft into your Gmail Drafts folder.");
    } catch (err) {
      console.error("Connect Gmail failed", err);
      setGmailMsg(`Couldn't connect: ${err?.message || err}`);
    }
    setGmailBusy(false);
  }

  async function handleDisconnectGmail() {
    disconnectGmail();
    await setGmailLinkedFlag(userProfile.companyId, false);
    setGmailLinked(false);
    setGmailMsg("Disconnected. Approve will no longer create Gmail drafts.");
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div className="page-header">
        <div>
          <div className="page-title">Settings</div>
          <div className="page-subtitle">Company configuration</div>
        </div>
        <button className="btn btn-blue" onClick={handleSave} disabled={saving}>
          {saved ? "Saved ✓" : saving ? "Saving..." : "Save changes"}
        </button>
      </div>

      <div className="page-body">
        {loading ? (
          <div className="loading-state">Loading...</div>
        ) : (
          <div style={{ maxWidth: 560 }}>
            {error && <div style={{ background: "#fff0f0", border: "1px solid #f5a5a5", borderRadius: 7, padding: "8px 12px", fontSize: 12, color: "#a32d2d", marginBottom: 14 }}>{error}</div>}
            {saved && <div className="success-bar" style={{ margin: "0 0 14px" }}>Settings saved successfully.</div>}

            <div className="panel" style={{ marginBottom: 16 }}>
              <div className="panel-header"><div className="panel-title">Company</div></div>
              <div className="field-row">
                <div className="field-label">Company name</div>
                <input value={form.name} onChange={(e) => set("name", e.target.value)} style={{ flex: 1 }} />
              </div>
              <div className="field-row">
                <div className="field-label">Hub airport code</div>
                <input value={form.hubAirport} onChange={(e) => set("hubAirport", e.target.value.toUpperCase())} style={{ width: 80 }} maxLength={4} placeholder="CLT" />
              </div>
              <div className="field-row" style={{ alignItems: "flex-start" }}>
                <div className="field-label">Hub cargo address</div>
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
                  <PlacesAutocomplete
                    value={form.hubAirportAddress}
                    onChange={(addr, lat, lng) => {
                      set("hubAirportAddress", addr);
                      // Only commit coords when the user picks from the
                      // dropdown (free text leaves them null and we fall back
                      // to constants/airports.js by airport code).
                      if (typeof lat === "number") {
                        set("hubAirportLat", lat);
                        set("hubAirportLng", lng);
                      }
                    }}
                    placeholder="e.g. 5500 Josh Birmingham Pkwy, Charlotte NC 28208"
                    style={{ width: "100%", padding: "6px 10px", border: "1px solid #ddd", borderRadius: 7 }}
                  />
                  <div style={{ fontSize: 10, color: "#888" }}>
                    Your cargo terminal — drives tap-to-navigate, mileage calc, and the address on workbook output.
                    {form.hubAirportLat != null && <span style={{ color: "#0E7C46", marginLeft: 6 }}>✓ coords saved</span>}
                  </div>
                </div>
              </div>
              <div className="field-row" style={{ alignItems: "flex-start" }}>
                <div className="field-label">Hub access notes</div>
                <textarea
                  value={form.hubAirportNotes}
                  onChange={(e) => set("hubAirportNotes", e.target.value)}
                  placeholder="e.g. Gate 4, ring buzzer, ID required"
                  style={{ flex: 1, minHeight: 50, padding: "6px 10px", border: "1px solid #ddd", borderRadius: 7, fontSize: 12 }}
                />
              </div>
            </div>

            <div className="panel" style={{ marginBottom: 16 }}>
              <div className="panel-header">
                <div>
                  <div className="panel-title">Flight numbers</div>
                  <div className="panel-subtitle">
                    Inbound = SDF → {form.hubAirport || "hub"} &nbsp;·&nbsp; Outbound = {form.hubAirport || "hub"} → SDF
                  </div>
                </div>
              </div>
              <div className="field-row">
                <div className="field-label">Inbound flight (SDF → {form.hubAirport || "hub"})</div>
                <input value={form.inboundFlight} onChange={(e) => set("inboundFlight", e.target.value.toUpperCase())} style={{ width: 160 }} placeholder="UPS5X1286" />
              </div>
              <div className="field-row" style={{ alignItems: "flex-start" }}>
                <div className="field-label">Inbound arrival time</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <input type="time" value={form.inboundArrivalTime}
                    onChange={(e) => set("inboundArrivalTime", e.target.value)}
                    style={{ width: 110 }} />
                  <div style={{ fontSize: 10, color: "#888" }}>
                    Auto-fills the airport stop's scheduled time on new inbound jobs
                    so you don't type 05:15 every morning. Per-day variation can come later.
                  </div>
                </div>
              </div>
              <div className="field-row">
                <div className="field-label">Outbound flight ({form.hubAirport || "hub"} → SDF)</div>
                <input value={form.outboundFlight} onChange={(e) => set("outboundFlight", e.target.value.toUpperCase())} style={{ width: 160 }} placeholder="UPS5X1285" />
              </div>
            </div>

            <div className="panel" style={{ marginBottom: 16 }}>
              <div className="panel-header">
                <div>
                  <div className="panel-title">Reminders</div>
                  <div className="panel-subtitle">
                    The day before each scheduled job, guards see a confirmation card
                    with a tap-to-text button to the rep. Push reminders (when added)
                    will fire at the time below. Per Orion FAQ: text the rep by 11am.
                  </div>
                </div>
              </div>
              <div className="field-row">
                <div className="field-label">Day-before reminder time</div>
                <input type="time" value={form.confirmReminderTime}
                  onChange={(e) => set("confirmReminderTime", e.target.value)}
                  style={{ width: 110 }} />
              </div>
            </div>

            {/* Gmail Drafts integration. When connected, approving a
                job in Ready to Bill creates a fully-composed draft in
                the owner's Gmail Drafts folder with the workbook + POD
                PDF attached. The owner clicks "Open in Gmail" from
                the invoice row, reviews, and hits Send. The OAuth
                scope is drafts-only (gmail.compose) — VeloceFlow
                cannot send mail without the human Send click. */}
            <div className="panel" style={{ marginBottom: 16 }}>
              <div className="panel-header">
                <div>
                  <div className="panel-title">Gmail (invoice drafts)</div>
                  <div className="panel-subtitle">
                    Auto-compose a Gmail draft (with workbook + POD attached) on every
                    Approve &amp; invoice. Drafts-only — VeloceFlow cannot send mail
                    on your behalf, just compose it.
                  </div>
                </div>
                {gmailLinked ? (
                  <button className="btn btn-sm" onClick={handleDisconnectGmail} disabled={gmailBusy}>
                    Disconnect
                  </button>
                ) : (
                  <button className="btn btn-blue btn-sm" onClick={handleConnectGmail} disabled={gmailBusy}>
                    {gmailBusy ? "Connecting..." : "Connect Gmail"}
                  </button>
                )}
              </div>
              <div className="field-row">
                <div className="field-label">Status</div>
                <div style={{ flex: 1, fontSize: 12 }}>
                  {gmailLinked ? (
                    <span style={{ color: "#0E7C46", fontWeight: 600 }}>✓ Connected</span>
                  ) : (
                    <span style={{ color: "#888" }}>Not connected — Approve still works, but no draft is composed.</span>
                  )}
                  {isGmailConnected() && (
                    <span style={{ marginLeft: 8, fontSize: 10, color: "#aaa" }}>
                      (access token cached this session)
                    </span>
                  )}
                </div>
              </div>
              {gmailMsg && (
                <div style={{
                  margin: "0 14px 12px", padding: "8px 12px",
                  background: gmailMsg.startsWith("Couldn't") ? "#FBEAEA" : "#E0F4E8",
                  border: `1px solid ${gmailMsg.startsWith("Couldn't") ? "#E8B4B4" : "#a9d4c2"}`,
                  borderRadius: 6, fontSize: 11,
                  color: gmailMsg.startsWith("Couldn't") ? "#A32D2D" : "#0E7C46",
                }}>{gmailMsg}</div>
              )}
              <div style={{ padding: "8px 14px", fontSize: 10, color: "#888", background: "#fafbfc", borderTop: "1px solid #f0f0f0" }}>
                Per-broker To: / Subject / Body live on each broker's rate card (Rate cards → broker → Gmail draft template).
                Leave the broker's <code>Invoice email</code> blank to skip auto-draft for that broker.
              </div>
            </div>

            {/* Owner home base — drives mileage start/end for Ferrari +
                Executive jobs (relaxed billing where mileage is door-to-
                door from the owner's house, not the company hub). */}
            <div className="panel" style={{ marginBottom: 16 }}>
              <div className="panel-header">
                <div>
                  <div className="panel-title">Your home base</div>
                  <div className="panel-subtitle">
                    Used as the start &amp; end point for auto-calculated
                    mileage on <strong>Ferrari</strong> and <strong>Executive</strong> jobs.
                    The route runs home → pickups → airport → home.
                  </div>
                </div>
                <button
                  className="btn btn-blue btn-sm"
                  onClick={handleSaveHome}
                  disabled={savingHome}
                >
                  {homeSaved ? "Saved ✓" : savingHome ? "Saving..." : "Save home base"}
                </button>
              </div>
              <div className="field-row" style={{ alignItems: "flex-start" }}>
                <div className="field-label">Home address</div>
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
                  <PlacesAutocomplete
                    value={homeForm.homeAddress}
                    onChange={(addr, lat, lng) => {
                      setHomeForm((f) => ({
                        ...f,
                        homeAddress: addr,
                        // Only commit coords on a real pick from the dropdown.
                        homeLat: typeof lat === "number" ? lat : f.homeLat,
                        homeLng: typeof lng === "number" ? lng : f.homeLng,
                      }));
                    }}
                    placeholder="Start typing your address..."
                    style={{ width: "100%", padding: "6px 10px", border: "1px solid #ddd", borderRadius: 7 }}
                  />
                  <div style={{ fontSize: 10, color: "#888" }}>
                    Stays on your user account, not the company. Other owners (if added later)
                    set their own.
                    {homeForm.homeLat != null && (
                      <span style={{ color: "#0E7C46", marginLeft: 6 }}>✓ coords saved</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="panel" style={{ marginBottom: 16 }}>
              <div className="panel-header">
                <div>
                  <div className="panel-title">Login view</div>
                  <div className="panel-subtitle">
                    Which screen opens when you log in. You can always switch with
                    “Switch to guard mode” / “Back to owner view”.
                  </div>
                </div>
              </div>
              <div style={{ padding: 14, display: "flex", gap: 8 }}>
                {[
                  { key: "owner", label: "🏠 Owner view" },
                  { key: "guard", label: "👁 Guard view" },
                ].map((opt) => {
                  const active = (userProfile?.defaultView || "owner") === opt.key;
                  return (
                    <button
                      key={opt.key}
                      onClick={() => setDefaultView(opt.key)}
                      disabled={savingView}
                      style={{
                        flex: 1, padding: "10px 12px", borderRadius: 8,
                        border: `1px solid ${active ? "#185FA5" : "#ddd"}`,
                        background: active ? "#E6F0FA" : "#fff",
                        color: active ? "#185FA5" : "#555",
                        fontWeight: active ? 700 : 500, fontSize: 13,
                        cursor: savingView ? "wait" : "pointer",
                      }}
                    >
                      {opt.label}{active ? " ✓" : ""}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="panel" style={{ marginBottom: 16 }}>
              <div className="panel-header"><div className="panel-title">Job calendar</div></div>
              <div style={{ padding: 14 }}>
                <CalendarSubscribe />
              </div>
            </div>

            <div className="panel">
              <div className="panel-header"><div className="panel-title">Account</div></div>
              <div className="field-row">
                <div className="field-label">Owner name</div>
                <div className="field-value">{userProfile?.name || "—"}</div>
              </div>
              <div className="field-row">
                <div className="field-label">Email</div>
                <div className="field-value">{userProfile?.email || "—"}</div>
              </div>
              <div className="field-row">
                <div className="field-label">Role</div>
                <div className="field-value" style={{ textTransform: "capitalize" }}>{userProfile?.role || "—"}</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
